import React, { useEffect, useMemo, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Tabs, TabsContent } from '../ui';
import { CELL_RENDERERS, renderDefault } from '../tables/tableCellRenderers';
import { getTableActionsColumnWidth } from '../../config/appConfig';
import { buildAdvancedTableVariant } from '../../config/tableUiTheme';
import {
  getActiveTableUiThemeId,
  subscribeTableUiTheme,
} from '../../config/tableUiThemeRuntime';
import { SplitViewTabBar } from './SplitViewWithTabs';

const ALL_TAB_VALUE = 'all';

function normalizeCategoryKey(value) {
  const s = (value ?? '').toString().trim();
  return s || 'general';
}

function renderCell(value, column, row, theme) {
  const renderer = column.cellRenderer && CELL_RENDERERS[column.cellRenderer];
  if (renderer) {
    return renderer(value, column, row, theme, {});
  }
  return renderDefault(value, column, row, theme, {});
}

const ROW_HEIGHT_BY_DENSITY = {
  compact: 'h-8',
  normal: 'h-10',
  comfortable: 'h-12',
};

/**
 * Settings list grouped by category tabs (All + one tab per category), split-view tab style.
 */
export function CategoryGroupedTable({
  data = [],
  columns = [],
  groupByField = 'category',
  formatCategoryLabel = (key) => key,
  renderActions,
  moduleConfig,
  className,
  density = 'normal',
  allTabLabel = 'All',
}) {
  const { theme } = useTheme();
  const [tableUiThemeId, setTableUiThemeId] = useState(getActiveTableUiThemeId);

  useEffect(() => subscribeTableUiTheme(setTableUiThemeId), []);

  const tableVariant = useMemo(
    () => buildAdvancedTableVariant(tableUiThemeId, theme),
    [tableUiThemeId, theme],
  );
  const isRefined = tableVariant.useZebraFromCss === true;

  const groups = useMemo(() => {
    const map = new Map();
    for (const row of data) {
      const key = normalizeCategoryKey(row[groupByField]);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(row);
    }

    const entries = Array.from(map.entries()).map(([key, items]) => ({
      key,
      label: formatCategoryLabel(key),
      items: [...items].sort((a, b) =>
        String(a.key || '').localeCompare(String(b.key || ''), undefined, { sensitivity: 'base' }),
      ),
    }));

    entries.sort((a, b) => {
      if (a.key === 'general') return -1;
      if (b.key === 'general') return 1;
      return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
    });

    return entries;
  }, [data, groupByField, formatCategoryLabel]);

  const allItems = useMemo(() => {
    return [...data].sort((a, b) => {
      const catA = formatCategoryLabel(normalizeCategoryKey(a[groupByField]));
      const catB = formatCategoryLabel(normalizeCategoryKey(b[groupByField]));
      const byCat = catA.localeCompare(catB, undefined, { sensitivity: 'base' });
      if (byCat !== 0) return byCat;
      return String(a.key || '').localeCompare(String(b.key || ''), undefined, {
        sensitivity: 'base',
      });
    });
  }, [data, groupByField, formatCategoryLabel]);

  const tabs = useMemo(
    () => [
      { value: ALL_TAB_VALUE, label: allTabLabel, badge: data.length },
      ...groups.map((g) => ({
        value: g.key,
        label: g.label,
        badge: g.items.length,
      })),
    ],
    [allTabLabel, data.length, groups],
  );

  const actionsWidth = moduleConfig ? getTableActionsColumnWidth(moduleConfig) : '140px';
  const hasActions = typeof renderActions === 'function';
  const visibleColumns = columns.filter((c) => c.key !== groupByField && c.key !== 'actions');

  const allTabColumns = useMemo(
    () => [
      {
        key: groupByField,
        header: 'Category',
        cellRenderer: 'default',
      },
      ...visibleColumns,
    ],
    [groupByField, visibleColumns],
  );

  const tableProps = {
    visibleColumns,
    hasActions,
    renderActions,
    actionsWidth,
    theme,
    tableVariant,
    isRefined,
    density,
  };

  if (data.length === 0) {
    return (
      <div className={cn('flex items-center justify-center h-full p-8', theme.text.muted, className)}>
        No settings match your filters.
      </div>
    );
  }

  return (
    <div
      className={cn('flex flex-col flex-1 min-h-0 h-full px-2 pt-3 pb-3', className)}
      data-table-ui={tableUiThemeId}
    >
      <Tabs defaultValue={ALL_TAB_VALUE} className="flex flex-col flex-1 min-h-0 gap-1">
        <SplitViewTabBar
          tabs={tabs}
          wrapperClassName="shrink-0 pt-0.5 pb-1.5"
          listClassName="gap-1.5 pb-1"
          triggerClassName="!border-b-0 px-3 py-2 min-h-[2.25rem] rounded-lg"
        />

        <TabsContent
          value={ALL_TAB_VALUE}
          className="!mt-2 flex-1 min-h-0 h-0 overflow-hidden flex flex-col"
        >
          <CategorySettingsTable
            items={allItems}
            {...tableProps}
            visibleColumns={allTabColumns}
            formatCategoryLabel={formatCategoryLabel}
            groupByField={groupByField}
          />
        </TabsContent>

        {groups.map((group) => (
          <TabsContent
            key={group.key}
            value={group.key}
            className="!mt-2 flex-1 min-h-0 h-0 overflow-hidden flex flex-col"
          >
            <CategorySettingsTable items={group.items} {...tableProps} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function CategorySettingsTable({
  items,
  visibleColumns,
  hasActions,
  renderActions,
  actionsWidth,
  theme,
  tableVariant,
  isRefined,
  density,
  formatCategoryLabel,
  groupByField,
}) {
  const densityClass = `density-${density}`;

  const scrollShell = isRefined
    ? cn(
        'category-grouped-table-scroll advanced-table-scroll flex-1 min-h-0 min-w-0 w-full max-w-full overflow-x-auto overflow-y-auto',
        'rounded-xl border border-slate-200/80 dark:border-slate-600/50',
        'bg-white/90 dark:bg-slate-800/35 shadow-sm',
      )
    : cn(
        'category-grouped-table-scroll flex-1 min-h-0 min-w-0 w-full max-w-full overflow-x-auto overflow-y-auto rounded-xl border',
        theme.colors.surface,
        theme.border.light,
        theme.shadow.lg,
      );

  const theadClass = isRefined
    ? 'advanced-table-header sticky top-0 z-10'
    : cn('border-b-2 sticky top-0 z-10', tableVariant.header(theme));

  const thBase = isRefined
    ? cn('advanced-table-th text-left', densityClass, tableVariant.thExtra())
    : cn(
        'relative select-none px-3 py-2.5 font-semibold text-xs uppercase tracking-wide text-left',
        'text-white',
        tableVariant.cellBorders(theme),
        densityClass,
      );

  const actionsThClass = isRefined
    ? cn(
        'advanced-table-th advanced-table-actions-th whitespace-nowrap',
        densityClass,
        tableVariant.thExtra(),
      )
    : cn(thBase, theme.gradients.header);

  if (items.length === 0) {
    return (
      <div className={cn('flex items-center justify-center py-12 text-sm', theme.text.muted)}>
        No settings in this category.
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 h-full">
      <div className={scrollShell}>
        <table className="w-full border-collapse min-w-[640px]">
        <thead className={theadClass}>
          <tr>
            {hasActions ? (
              <th
                className={actionsThClass}
                style={{ width: actionsWidth, maxWidth: actionsWidth }}
              >
                Actions
              </th>
            ) : null}
            {visibleColumns.map((col) => (
              <th key={col.key} className={thBase}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((row, rowIndex) => (
            <tr
              key={row.id ?? row.key ?? rowIndex}
              className={
                isRefined
                  ? cn('advanced-table-row group', densityClass)
                  : cn(
                      'group transition-all duration-150',
                      tableVariant.rowBase(theme, {
                        rowHeight: ROW_HEIGHT_BY_DENSITY[density] || ROW_HEIGHT_BY_DENSITY.normal,
                      }),
                      tableVariant.getRowClassName?.(rowIndex, theme),
                    )
              }
            >
              {hasActions ? (
                <td
                  className={
                    isRefined
                      ? cn(
                          'advanced-table-actions-td sticky-actions-column',
                          densityClass,
                          theme.colors.surface,
                        )
                      : cn(
                          'px-2 py-2 align-middle border-r',
                          theme.border.light,
                          theme.colors.surface,
                        )
                  }
                  style={{ width: actionsWidth, maxWidth: actionsWidth }}
                >
                  <div className="flex justify-center">{renderActions(row)}</div>
                </td>
              ) : null}
              {visibleColumns.map((col) => {
                let value = col.displayField ? row[col.displayField] : row[col.key];
                if (
                  col.key === groupByField &&
                  typeof formatCategoryLabel === 'function'
                ) {
                  value = formatCategoryLabel(normalizeCategoryKey(row[groupByField]));
                }
                return (
                  <td
                    key={col.key}
                    className={
                      isRefined
                        ? cn(
                            'advanced-table-td align-middle max-w-[320px]',
                            densityClass,
                            theme.text.primary,
                          )
                        : cn(
                            'px-3 py-2 align-middle max-w-[320px] border-r last:border-r-0',
                            theme.border.light,
                            theme.text.primary,
                            densityClass,
                          )
                    }
                  >
                    {renderCell(value, col, row, theme)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        </table>
      </div>
    </div>
  );
}

export default CategoryGroupedTable;
